import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  AdminAllocationStateResponseV1,
  AdminBalanceMutationResponseV1,
  AdminBalanceSummaryResponseV1,
  AdminLedgerRowResponseV1,
  AdminMembershipResponseV1,
  AdminUsageQueryParams,
  AdminUsageQueryResponseV1,
  AdminUserEntitlementsResponseV1,
  SetMembershipExpirationRequestV1,
  SetMembershipExpirationResponseV1,
  UpdateBalanceQuantityRequestV1
} from '@entitlements/entitlements';
import { MessageService } from 'primeng/api';
import { catchError, map, Observable, of, throwError } from 'rxjs';

import { ServiceResponseV1 } from '@core/models/apis';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EntitlementsService {
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);

  private readonly apiUrl = `${environment.apiUrl}${environment.apis.iamEngineAdmin}/v1/entitlements`;

  // DELETE /usages/{id} (ReleaseEntitlement) and POST /usages/{id}/release (AdminForceRelease) both
  // route to the same release-or-forfeit behavior on the admin surface — we only wire the POST form
  // below; there's no separate "release" method to add.

  getUserEntitlements(userId: string): Observable<AdminUserEntitlementsResponseV1> {
    return this.http.get<ServiceResponseV1<AdminUserEntitlementsResponseV1>>(`${this.apiUrl}/users/${userId}`).pipe(
      map((response) => response.data ?? { userId, allocations: [], count: 0 }),
      catchError((error) => this.errorHandler(error, 'Error loading entitlements for user'))
    );
  }

  getMembership(membershipId: string): Observable<AdminMembershipResponseV1> {
    return this.http
      .get<ServiceResponseV1<AdminMembershipResponseV1>>(`${this.apiUrl}/memberships/${membershipId}`)
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error loading membership'))
      );
  }

  setMembershipExpiration(
    membershipId: string,
    body: SetMembershipExpirationRequestV1
  ): Observable<SetMembershipExpirationResponseV1> {
    return this.http
      .patch<
        ServiceResponseV1<SetMembershipExpirationResponseV1>
      >(`${this.apiUrl}/memberships/${membershipId}/expiration`, body)
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error setting membership expiration'))
      );
  }

  queryUsages(params: AdminUsageQueryParams): Observable<AdminUsageQueryResponseV1> {
    let httpParams = new HttpParams();
    if (params.idempotencyKey) httpParams = httpParams.set('idempotencyKey', params.idempotencyKey);
    if (params.groupId) httpParams = httpParams.set('groupId', params.groupId);
    if (params.membershipId) httpParams = httpParams.set('membershipId', params.membershipId);
    if (params.featureCode) httpParams = httpParams.set('featureCode', params.featureCode);
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.heldOlderThanMinutes !== undefined) {
      httpParams = httpParams.set('heldOlderThanMinutes', String(params.heldOlderThanMinutes));
    }

    return this.http
      .get<ServiceResponseV1<AdminUsageQueryResponseV1>>(`${this.apiUrl}/usages`, { params: httpParams })
      .pipe(
        map((response) => response.data ?? { usages: [], count: 0 }),
        catchError((error) => this.errorHandler(error, 'Error querying usage ledger'))
      );
  }

  getLedgerRow(usageId: string): Observable<AdminLedgerRowResponseV1> {
    return this.http.get<ServiceResponseV1<AdminLedgerRowResponseV1>>(`${this.apiUrl}/usages/${usageId}`).pipe(
      map((response) => response.data!),
      catchError((error) => this.errorHandler(error, 'Error loading usage'))
    );
  }

  getBalance(allocationId: string): Observable<AdminBalanceSummaryResponseV1> {
    return this.http
      .get<ServiceResponseV1<AdminBalanceSummaryResponseV1>>(`${this.apiUrl}/allocations/${allocationId}/balance`)
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error loading allocation balance'))
      );
  }

  forceCommit(usageId: string): Observable<AdminLedgerRowResponseV1> {
    return this.http
      .post<ServiceResponseV1<AdminLedgerRowResponseV1>>(`${this.apiUrl}/usages/${usageId}/commit`, {})
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error force-committing usage'))
      );
  }

  forceRelease(usageId: string): Observable<AdminLedgerRowResponseV1> {
    return this.http
      .post<ServiceResponseV1<AdminLedgerRowResponseV1>>(`${this.apiUrl}/usages/${usageId}/release`, {})
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error force-releasing usage'))
      );
  }

  forceForfeit(usageId: string): Observable<AdminLedgerRowResponseV1> {
    return this.http
      .post<ServiceResponseV1<AdminLedgerRowResponseV1>>(`${this.apiUrl}/usages/${usageId}/forfeit`, {})
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error force-forfeiting usage'))
      );
  }

  updateBalanceQuantity(allocationId: string, quantity: number): Observable<AdminBalanceMutationResponseV1> {
    const body: UpdateBalanceQuantityRequestV1 = { quantity };
    return this.http
      .patch<
        ServiceResponseV1<AdminBalanceMutationResponseV1>
      >(`${this.apiUrl}/allocations/${allocationId}/balance/quantity`, body)
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error updating balance cap'))
      );
  }

  forceRefreshBalance(allocationId: string): Observable<AdminBalanceMutationResponseV1> {
    return this.http
      .post<
        ServiceResponseV1<AdminBalanceMutationResponseV1>
      >(`${this.apiUrl}/allocations/${allocationId}/balance/refresh`, {})
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error force-refreshing balance'))
      );
  }

  deactivateAllocation(allocationId: string): Observable<AdminAllocationStateResponseV1> {
    return this.http
      .post<
        ServiceResponseV1<AdminAllocationStateResponseV1>
      >(`${this.apiUrl}/allocations/${allocationId}/deactivate`, {})
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error deactivating allocation'))
      );
  }

  reactivateAllocation(allocationId: string): Observable<AdminAllocationStateResponseV1> {
    return this.http
      .post<
        ServiceResponseV1<AdminAllocationStateResponseV1>
      >(`${this.apiUrl}/allocations/${allocationId}/reactivate`, {})
      .pipe(
        map((response) => response.data!),
        catchError((error) => this.errorHandler(error, 'Error reactivating allocation'))
      );
  }

  createUserEntitlementsResource(userId: () => string | null | undefined) {
    return rxResource<AdminUserEntitlementsResponseV1 | null, string | null | undefined>({
      params: userId,
      stream: ({ params }) => (params ? this.getUserEntitlements(params) : of(null))
    });
  }

  createUsagesResource(params: () => AdminUsageQueryParams) {
    return rxResource<AdminUsageQueryResponseV1, AdminUsageQueryParams>({
      params,
      stream: ({ params }) => this.queryUsages(params)
    });
  }

  createLedgerRowResource(usageId: () => string | null | undefined) {
    return rxResource<AdminLedgerRowResponseV1 | null, string | null | undefined>({
      params: usageId,
      stream: ({ params }) => (params ? this.getLedgerRow(params) : of(null))
    });
  }

  createBalanceResource(allocationId: () => string | null | undefined) {
    return rxResource<AdminBalanceSummaryResponseV1 | null, string | null | undefined>({
      params: allocationId,
      stream: ({ params }) => (params ? this.getBalance(params) : of(null))
    });
  }

  private errorHandler(error: unknown, serviceMessage: string): Observable<never> {
    const asHttp = error as { error?: { message?: string }; message?: string };
    const message =
      error instanceof Error ? error.message : (asHttp?.error?.message ?? asHttp?.message ?? 'Unknown error');
    console.error(`[EntitlementsService] ${serviceMessage}, ${message}`);
    this.messageService.add({ severity: 'error', summary: 'Error', detail: serviceMessage });
    return throwError(() => error);
  }
}
